
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { format } from "date-fns";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  categories: string;
  featured_image_url: string;
  publication_date: string;
}

export const BlogSection = () => {
  const { data: posts = [], isLoading } = useQuery({
    queryKey: ['published-blog-posts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('id, title, slug, excerpt, categories, featured_image_url, publication_date')
        .eq('status', 'published')
        .order('publication_date', { ascending: false })
        .limit(3);
      
      if (error) {
        console.error('Error fetching blog posts:', error);
        return [];
      }
      return data as BlogPost[];
    },
  });

  // Fallback articles if no posts are found
  const fallbackArticles = [
    {
      title: "Why Orizis AI Studio? - Founder's Message",
      excerpt: "Much like the internet revolution of the 1990s and 2000s, when having a website...",
      category: "ARTICLE",
      featured: false
    },
    {
      title: "Vertical AI Agents > 10x Bigger Than SaaS",
      excerpt: "The parallels between early SaaS and Vertical AI Agents SaaS: Cloud-based...",
      category: "ARTICLE",
      featured: false
    },
    {
      title: "AI Phone Agents For Property Management",
      excerpt: "How AI phone agents are transforming property management AI phone calling...",
      category: "ARTICLE",
      featured: true
    }
  ];

  const articlesToShow = posts.length > 0 ? posts : fallbackArticles;

  return (
    <section id="blog" className="py-20 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-16">
          <div>
            <div className="text-sm text-purple-500 font-medium mb-4">003</div>
            <h2 className="text-4xl md:text-5xl font-light text-gray-900">
              Insights, stories, and trends shaping AI
            </h2>
          </div>
          <Link to="/blog">
            <Button 
              variant="outline" 
              className="hidden md:flex border-gray-300 text-gray-900 hover:bg-gray-50"
            >
              VIEW ALL
            </Button>
          </Link>
        </div>
        
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-8">
            {articlesToShow.map((article, index) => {
              // Handle both database posts and fallback articles
              const isDbPost = 'slug' in article;
              const title = isDbPost ? article.title : article.title;
              const excerpt = isDbPost ? article.excerpt : article.excerpt;
              const category = isDbPost ? (article.categories?.split(',')[0] || 'ARTICLE') : article.category;
              const featured = isDbPost ? index === 0 : article.featured;
              
              // Fix the URL construction to prevent double slashes
              const postUrl = isDbPost ? `/blog/${article.slug.replace(/^\/+/, '')}` : '#';
              const featuredImage = isDbPost ? article.featured_image_url : null;
              
              return (
                <Link 
                  key={isDbPost ? article.id : index} 
                  to={postUrl}
                  className="group cursor-pointer block"
                >
                  <div className={`rounded-lg mb-6 h-64 flex items-center justify-center overflow-hidden ${
                    !featuredImage ? (featured ? 'bg-purple-500' : 'bg-gray-100') : ''
                  }`}>
                    {featuredImage ? (
                      <img 
                        src={featuredImage} 
                        alt={title}
                        className="w-full h-full object-cover"
                      />
                    ) : featured ? (
                      <div className="text-white text-center">
                        <div className="text-2xl font-light mb-2">Orizis</div>
                        <div className="text-xl font-medium px-4">{title}</div>
                      </div>
                    ) : (
                      <div className="w-full h-full bg-gray-200 rounded-lg flex items-center justify-center">
                        <div className="text-black font-bold text-lg">Orizis</div>
                      </div>
                    )}
                  </div>
                  
                  <div className="text-xs text-gray-500 font-medium mb-3 uppercase">
                    {category}
                  </div>
                  
                  <h3 className="text-xl font-medium text-gray-900 mb-3 group-hover:text-gray-600 transition-colors">
                    {title}
                  </h3>
                  
                  <p className="text-gray-600 text-sm">
                    {excerpt}
                  </p>
                  
                  {isDbPost && (
                    <div className="text-xs text-gray-400 mt-2">
                      {format(new Date(article.publication_date), 'MMM dd, yyyy')}
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};
